/**
 * Serialization utilities for converting between React Flow graph state
 * and the backend WorkflowTemplate schema.
 */

/**
 * Convert React Flow nodes + edges → backend WorkflowTemplate format.
 *
 * Backend WorkflowNode schema:
 *   { id, name, service_type, config, inputs, dependencies }
 *
 * Mapping:
 *   - node.id → id
 *   - node.data.name → name
 *   - node.type → service_type
 *   - node.data.config → config (with position metadata)
 *   - node.data.inputs → inputs
 *   - edges where target === node.id → dependencies (source IDs)
 */
export function serializeGraph(nodes, edges) {
  return nodes.map((node) => {
    // Find all edges pointing TO this node → those sources are dependencies
    const dependencies = edges
      .filter((e) => e.target === node.id)
      .map((e) => e.source);

    return {
      id: node.id,
      name: node.data.name || node.data.label || 'Untitled',
      service_type: node.type || node.data.serviceType || 'mock',
      config: {
        ...(node.data.config || {}),
        // Persist position for layout restoration
        _position: { x: node.position.x, y: node.position.y },
      },
      inputs: node.data.inputs || {},
      dependencies,
    };
  });
}

/**
 * Convert backend WorkflowTemplate → React Flow nodes + edges.
 *
 * @param {Object} template - Backend template with `nodes` array.
 * @returns {{ nodes: Array, edges: Array, variables: Array }}
 */
export function deserializeGraph(template) {
  const templateNodes = template.nodes || [];

  // Convert to React Flow nodes
  const nodes = templateNodes.map((n, index) => {
    const position = n.config?._position || {
      x: 250,
      y: 80 + index * 150,  // Stack vertically if no position saved
    };

    // Remove internal _position from config so it doesn't pollute the form
    const config = { ...n.config };
    delete config._position;

    return {
      id: n.id,
      type: n.service_type || 'mock',
      position,
      data: {
        label: n.name,
        name: n.name,
        serviceType: n.service_type || 'mock',
        config,
        inputs: n.inputs || {},
      },
    };
  });

  // Convert dependencies → edges
  const edges = [];
  templateNodes.forEach((n) => {
    (n.dependencies || []).forEach((depId) => {
      edges.push({
        id: `e-${depId}-${n.id}`,
        source: depId,
        target: n.id,
        animated: true,
        style: { stroke: '#6366F1', strokeWidth: 2 },
        markerEnd: { type: 'arrowclosed', color: '#6366F1' },
      });
    });
  });

  // Surface template-scoped variable definitions so the builder can load them
  // back into the store (serializeGraph is unchanged — variables travel in the
  // save payload, not the serialized nodes).
  const variables = template.variables || [];

  return { nodes, edges, variables };
}
